import { useCallback, useEffect, useReducer, useState } from "react";
import { ConfirmProvider } from "material-ui-confirm";
import Snackbar from "@mui/material/Snackbar";
import Editor from "./components/Editor";
import Splash from "./components/Splash";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import { usePersistence } from "./hooks/usePersistence";
import { motionVars } from "./lib/motion";
import { isNative, takePendingSharedArticle } from "./lib/platform";
import {
  canRedo,
  canUndo,
  initialState,
  reducer,
  type ErasureDocument,
} from "./state/reducer";

import "./App.css";
import "./motion.css";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

const App = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [sharedInput, setSharedInput] = useState<string | null>(null);
  const isOnline = useOnlineStatus();

  const onSaveError = useCallback(() => {
    setSaveError("Couldn't save your changes. Your browser may be out of storage.");
  }, []);

  usePersistence(state.document, onSaveError);

  const open = useCallback((document: ErasureDocument) => {
    dispatch({ type: "openDocument", document });
  }, []);

  // Stable, because it reaches the memoised toolbar: an arrow written inline
  // here was a new function on every erased word, and redrew the SpeedDial, its
  // ten actions and their tooltips with each one — half the main thread's time
  // during a stroke.
  const close = useCallback(() => dispatch({ type: "closeDocument" }), []);

  useEffect(() => {
    if (!isNative()) return;

    let disposed = false;
    let removeListener: (() => Promise<void>) | undefined;
    const receive = async () => {
      if (disposed) return;
      const articleUrl = await takePendingSharedArticle();
      if (articleUrl && !disposed) {
        dispatch({ type: "closeDocument" });
        setSharedInput(articleUrl);
      }
    };

    void import("@capacitor/app").then(async ({ App: CapacitorApp }) => {
      if (disposed) return;
      await receive();
      const listener = await CapacitorApp.addListener(
        "appStateChange",
        ({ isActive }) => {
          if (isActive) void receive();
        },
      );
      if (disposed) await listener.remove();
      else removeListener = listener.remove;
    });

    return () => {
      disposed = true;
      void removeListener?.();
    };
  }, []);

  return (
    <ConfirmProvider>
      {/* The timeline's durations live here so every animated element below,
          on either screen, is playing against the same clock. */}
      <div className="App" style={motionVars()}>
        {state.document ? (
          <Editor
            document={state.document}
            dispatch={dispatch}
            canUndo={canUndo(state)}
            canRedo={canRedo(state)}
            onClose={close}
          />
        ) : (
          <Splash
            isOnline={isOnline}
            onOpen={open}
            sharedInput={sharedInput}
            onSharedInputConsumed={() => setSharedInput(null)}
          />
        )}
      </div>
      <Snackbar
        open={saveError !== null}
        autoHideDuration={6000}
        onClose={() => setSaveError(null)}
        message={saveError ?? ""}
      />
    </ConfirmProvider>
  );
};

export default App;
